import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Select,
  TextInput,
  Group,
  ActionIcon,
  Text,
} from "@mantine/core";
import { DateInput } from '@mantine/dates';
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import ApiSession from "../../api";
import { SeIcon } from "@se/web-ui-react";
import "./userTable.css";
import { useIsLightMode } from "../../utils/helpers";
import { IconPlus, IconTrash, IconEye, IconEdit } from "@tabler/icons-react";
import { useDynamicTableHeight } from "../../hooks/useDynamicTableHeight";
import { useAtom } from "jotai";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom";
import { useDualTranslation } from "../../utils/i18nHelpers";

const apiSession = new ApiSession();

const UserTable = () => {
  const { t } = useDualTranslation();
  const [userScope] = useAtom(userScopeAtom);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("A");
  const [activeUsers, setActiveUsers] = useState([]);
  const [inActiveUsers, setInActiveUsers] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [cancelledUsers, setCancelledUsers] = useState([]);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteDate, setInviteDate] = useState("");
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [roleOptions, setRoleOptions] = useState([]);
  const [inviteList, setInviteList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSubscriptionDate, setEditSubscriptionDate] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  // State for re-invite modal
  const [reinviteModalOpen, setReinviteModalOpen] = useState(false);
  const [reinviteUser, setReinviteUser] = useState(null);
  const [reinviteDate, setReinviteDate] = useState("");
  const [reinviteError, setReinviteError] = useState("");
  const [reinviteSelectedRole, setReinviteSelectedRole] = useState("");

  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });

  const isLightMode = useIsLightMode();

  useEffect(() => {
    setLoading(true);
    apiSession
      .getUsers()
      .then((response) => {
        const active = [];
        const inactive = [];
        const invited = [];
        const cancelled = [];
        (response || []).map((user) => {
          if (user.status === "A") {
            active.push(user);
          } else if (user.status === "D") {
            inactive.push(user);
          } else if (user.status === "I" || user.status === "E") {
            invited.push(user);
          } else if (user.status === "C") {
            cancelled.push(user);
          }
          return null;
        });
        setActiveUsers(active);
        setInActiveUsers(inactive);
        setInvitedUsers(invited);
        setCancelledUsers(cancelled);
        setUsers(response || []);
        setLoading(false);
      })
      .catch(() => {
        setUsers([]);
        setActiveUsers([]);
        setInActiveUsers([]);
        setInvitedUsers([]);
        setCancelledUsers([]);
        setLoading(false);
      });
  }, []);

  // Add data-intro to table header elements (similar to dataTable component)
  useEffect(() => {
    const theads = document.querySelectorAll('.mantine-Table-root thead');
    theads.forEach(thead => {
      if (!thead.getAttribute('data-intro')) {
          thead.setAttribute('data-intro', t('userManagement.tableHeaderIntro'));
        thead.setAttribute('data-position', 'bottom');
      }
    });
  }, []);

  // Load roles once when the component mounts so we don't call getRoles repeatedly
  useEffect(() => {
    let mounted = true;
    const loadRoles = async () => {
      try {
        setLoading(true);
        const response = await apiSession.getRoles();
        if (response && mounted) {
          setRoles(response);
          setRoleOptions(
            response.map((role) => ({
              value: role.id,
              label: `${role.role} - ${role.description || ""}`,
            }))
          );
        }
      } catch (e) {
        if (mounted) setRoleOptions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadRoles();
    return () => { mounted = false; };
  }, []);
  const { pageSize, getTableHeight } = useDynamicTableHeight({
    rowHeight: 40,
    headerHeight: 48,
    footerHeight: 48,
    reservedSpace: 170,
    minRows: 5,
  });

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageSize }));
  }, [pageSize]);

  const statusOptions = [
    { value: "A", label: t("userManagement.userTable.status.active") },
    { value: "I", label: t("userManagement.userTable.status.invited") },
    { value: "D", label: t("userManagement.userTable.status.deleted") },
    { value: "C", label: t("userManagement.userTable.status.cancelled") },
  ];

  const getFilteredUsers = () => {
    if (statusFilter === "A") return activeUsers;
    if (statusFilter === "D") return inActiveUsers;
    if (statusFilter === "I") return invitedUsers;
    if (statusFilter === "C") return cancelledUsers;
    return users;
  };

  const filteredUsers = getFilteredUsers();

  const editRoleRequiresDate = () => {
    const r = roles.find(rr => rr.role === editRole || rr.id === editRole);
    return !!(r && r.role && r.role.toLowerCase() !== 'secadmin');
  };

  const columns = [
    {
      accessorKey: "userId",
      header: t("userManagement.userTable.columns.id"),
      Cell: ({ cell }) => String(cell.getValue() ?? ""),
    },
    // Hide First Name column for Invited filter
    ...(statusFilter === "I"
      ? []
      : [{
          accessorKey: "name",
          header: t("userManagement.userTable.columns.firstName"),
          Cell: ({ cell }) => String(cell.getValue() ?? ""),
        }]),
    {
      accessorKey: "email",
      header: t("userManagement.userTable.columns.email"),
      Cell: ({ cell }) => String(cell.getValue() ?? ""),
    },
    {
      accessorKey: "employer",
      header: t("userManagement.userTable.columns.company"),
      Cell: ({ cell }) => String(cell.getValue() ?? ""),
    },
    {
      accessorKey: "role",
      header: t("userManagement.userTable.columns.role"),
      Cell: ({ cell }) => String(cell.getValue() ?? ""),
    },
    {
      accessorKey: "phoneNumber",
      header: t("userManagement.userTable.columns.phoneNumber"),
      Cell: ({ cell }) => String(cell.getValue() ?? ""),
    },
    {
      header: t("common.action"),
      accessorKey: "actions",
      Cell: ({ row }) => (
        <Group
          spacing={8}
          {...(row.index === 0
            ? {
                'data-intro': t('userManagement.actionIconsIntro'),
                'data-position': 'left',
              }
            : {})}
        >
            <ActionIcon
              color="seGreen.0"
              aria-label={`view-user-${row.index}`}
              onClick={() => {
                setSelectedUser(row.original);
                setViewModalOpen(true);
              }}
              size="lg"
              title={t("userManagement.userTable.actions.viewUser")}
              style={isLightMode ? { backgroundColor: 'transparent', transition: 'background 0.2s' } : {}}
              className={isLightMode ? 'user-action-icon-light' : ''}
            >
              <IconEye size={20} />
            </ActionIcon>
          {/* Show edit icon only for non-invited users, show re-invite for invited/cancelled/deleted */}
          {(statusFilter === "I" || statusFilter === "C" || statusFilter === "D") ? (
            <ActionIcon
              color="seGreen.0"
              aria-label={`reinvite-user-${row.index}`}
              onClick={() => handleReinviteUser(row.original)}
              size="lg"
              title={t("userManagement.userTable.actions.reinviteUser")}
              style={isLightMode ? { backgroundColor: 'transparent', transition: 'background 0.2s' } : {}}
              className={isLightMode ? 'user-action-icon-light' : ''}
            >
              <SeIcon>user_add</SeIcon>
            </ActionIcon>
          ) : statusFilter !== "I" ? (
            <ActionIcon
              color="seGreen.0"
              aria-label={`edit-user-${row.index}`}
              onClick={async () => {
                // Use already-loaded roles (fetched on mount)
                setEditUser(row.original);
                setEditRole(row.original.role || row.original.roleId || "");
                // Pre-populate subscription end date if available on user
                if (row.original.subscriptionEndDate) {
                  try {
                    const dt = new Date(row.original.subscriptionEndDate);
                    if (!isNaN(dt)) setEditSubscriptionDate(dt);
                  } catch (e) {
                    setEditSubscriptionDate("");
                  }
                } else {
                  setEditSubscriptionDate("");
                }
                setEditError("");
                setEditSubmitting(false);
                setEditModalOpen(true);
                setLoading(false);
              }}
              size="lg"
              title={t("userManagement.userTable.actions.editRole")}
              style={isLightMode ? { backgroundColor: 'transparent', transition: 'background 0.2s' } : {}}
              className={isLightMode ? 'user-action-icon-light' : ''}
            >
              <IconEdit size={20} />
            </ActionIcon>
          ) : null}
          {statusFilter !== "D" && statusFilter !== "C" && (
            <ActionIcon
              disabled={!(userScope && userScope.access && userScope.access.addUsers)}
              color="seGreen.0"
              aria-label={`delete-user-${row.index}`}
              onClick={() => {
                setDeleteUser(row.original);
                setDeleteModalOpen(true);
              }}
              size="lg"
              title={t("userManagement.userTable.actions.deleteUser")}
              style={isLightMode ? { backgroundColor: 'transparent', transition: 'background 0.2s' } : {}}
              className={isLightMode ? 'user-action-icon-light' : ''}
            >
              <IconTrash size={20} />
            </ActionIcon>
          )}
        </Group>
      ),
      size: 120,
      enableColumnActions: false,
      enableSorting: false,
    },
  ];

  // Handler for re-invite user
  // Show modal for re-invite confirmation
  const handleReinviteUser = async (user) => {
    // Use roles loaded on mount. If roles are missing, show an error.
    if (!roles || roles.length === 0) {
      callSnackbar('error', t('userManagement.rolesNotLoaded'));
      return;
    }
    setReinviteUser(user);
    setReinviteDate("");
    setReinviteError("");
    // Determine default role selection from user (prefer roleId then role name)
    let defaultRole = "";
    if (user) {
      if (user.roleId) defaultRole = user.roleId;
      else if (user.role) {
        const found = roles.find(r => r.role === user.role || r.id === user.role);
        defaultRole = found ? found.id : user.role;
      }
    }
    setReinviteSelectedRole(defaultRole);
    setReinviteModalOpen(true);
  };

  // Confirm re-invite action
  const handleConfirmReinvite = async () => {
    if (!reinviteUser) return;
    setSubmitting(true);
    try {
      let roleId = reinviteSelectedRole || reinviteUser.roleId;
      // Use roles loaded on mount
      const rolesList = roles;
        if (!rolesList || rolesList.length === 0) {
        setReinviteError(t('userManagement.rolesNotLoaded'));
        setSubmitting(false);
        return;
      }
      // Map role name to id if needed
      // If a role was explicitly selected in modal, prefer that. Otherwise fallback to user role mapping.
      if ((!roleId || roleId === '') && reinviteUser.role && rolesList && rolesList.length > 0) {
        const foundRole = rolesList.find(
          (role) => role.id === reinviteUser.roleId || role.role === reinviteUser.role
        );
        if (foundRole) roleId = foundRole.id;
      }
      const roleObj = rolesList.find(r => r.id === roleId);
      const requiresDate = roleObj && roleObj.role && roleObj.role.toLowerCase() !== 'secadmin';
      if (requiresDate && !reinviteDate) {
        setReinviteError(t('userManagement.reinvite.dateRequired'));
        setSubmitting(false);
        return;
      }
      const payload = { email: reinviteUser.email, roleId };
      if (requiresDate && reinviteDate) {
        let dateObj = typeof reinviteDate === 'string' ? new Date(reinviteDate) : reinviteDate;
        if (dateObj instanceof Date && !isNaN(dateObj)) {
          const yyyy = dateObj.getFullYear();
          const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
          const dd = String(dateObj.getDate()).padStart(2, '0');
          payload.endDate = `${yyyy}-${mm}-${dd}`;
        } else {
          payload.endDate = reinviteDate;
        }
      }
      await apiSession.addUsers(payload);
      setReinviteModalOpen(false);
      setReinviteUser(null);
      setReinviteDate("");
      setReinviteError("");
      setReinviteSelectedRole("");
      // Refresh user list after successful re-invite
      await refreshUsers();
      callSnackbar("success", t('userManagement.reinvite.sent', { email: reinviteUser.email }));
    } catch (e) {
      callSnackbar("error", t('userManagement.reinvite.failed'));
      setReinviteSelectedRole("");
    } finally {
      setSubmitting(false);
    }
  };
  const callSnackbar = (errorType, Body) => {
    setNotification({ show: true, type: errorType, message: Body });
    if (document && document.getElementById("snackbar-register")) {
      document.getElementById("snackbar-register").open = true;
    }
  };

  // Centralized helper to refresh users lists from API
  const refreshUsers = async () => {
    try {
      setLoading(true);
      const response = await apiSession.getUsers();
      const active = [];
      const inactive = [];
      const invited = [];
      const cancelled = [];
      (response || []).map((user) => {
        if (user.status === "A") {
          active.push(user);
        } else if (user.status === "D") {
          inactive.push(user);
        } else if (user.status === "I" || user.status === "E") {
          invited.push(user);
        } else if (user.status === "C") {
          cancelled.push(user);
        }
        return null;
      });
      setActiveUsers(active);
      setInActiveUsers(inactive);
      setInvitedUsers(invited);
      setCancelledUsers(cancelled);
      setUsers(response || []);
    } catch (err) {
      setUsers([]);
      setActiveUsers([]);
      setInActiveUsers([]);
      setInvitedUsers([]);
      setCancelledUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const addAsset = async () => {
    // Roles are loaded on mount. If not available, show error.
    if (!roles || roles.length === 0) {
      callSnackbar("error", t('userManagement.rolesNotLoaded'));
      return;
    }
    setInviteModalOpen(true);
  };

  const changeUserStatus = (value) => {
    setStatusFilter(value);
  };

  const handleAddInvite = async () => {
    if (!inviteEmail || !selectedRole) return;
    // If role is not secadmin, require date
    const selectedRoleObj = roles.find(
      (role) => role.id === selectedRole || role.role === selectedRole
    );
    if (selectedRoleObj && selectedRoleObj.role.toLowerCase() !== "secadmin" && !inviteDate) {
      setInviteError(t('userManagement.invite.dateRequired'));
      return;
    }
    // Check if email already exists in users list
    const emailExists = users.some(
      (user) => user.email.toLowerCase() === inviteEmail.toLowerCase()
    );
    if (emailExists) {
      callSnackbar("error", t('userManagement.invite.emailExists'));
      return;
    }
    // Map selectedRole to roleId using loaded roles
    let roleId = selectedRole;
    if (!roles || roles.length === 0) {
      callSnackbar('error', t('userManagement.rolesNotLoaded'));
      return;
    }
    const foundRole = roles.find(
      (role) => role.id === selectedRole || role.role === selectedRole
    );
    if (foundRole) roleId = foundRole.id;
    // Submit invite directly
    setSubmitting(true);
    try {
      // Pass date as endDate only if email is present
      const payload = { email: inviteEmail, roleId };
      if (inviteEmail && selectedRoleObj && selectedRoleObj.role.toLowerCase() !== "secadmin" && inviteDate) {
        // Format date to yyyy-mm-dd
        let dateObj = typeof inviteDate === 'string' ? new Date(inviteDate) : inviteDate;
        if (dateObj instanceof Date && !isNaN(dateObj)) {
          const yyyy = dateObj.getFullYear();
          const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
          const dd = String(dateObj.getDate()).padStart(2, '0');
          payload.endDate = `${yyyy}-${mm}-${dd}`;
        } else {
          payload.endDate = inviteDate;
        }
      }
      await apiSession.addUsers(payload);
      setInviteModalOpen(false);
      setInviteEmail("");
      setSelectedRole("");
      setInviteDate("");
      setInviteError("");
      // Refresh user list after successful invite
      await refreshUsers();
      callSnackbar("success", t('userManagement.invite.success'));
    } catch (e) {
      callSnackbar("error", t('userManagement.invite.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveInvite = (index) => {
    setInviteList(inviteList.filter((_, i) => i !== index));
  };

  const handleSubmitInvites = async () => {
    if (inviteList.length === 0) return;
    setSubmitting(true);
    try {
      // Ensure all inviteList items have correct roleId
        if (!roles || roles.length === 0) {
        callSnackbar('error', t('userManagement.rolesNotLoaded'));
        setSubmitting(false);
        return;
      }
      const updatedInviteList = inviteList.map((item) => {
        const foundRole = roles.find(
          (role) => role.id === item.roleId || role.role === item.roleId
        );
        return foundRole ? { ...item, roleId: foundRole.id } : item;
      });
      await apiSession.addUsers(updatedInviteList[0]);
      setInviteList([]);
      setInviteModalOpen(false);
      setInviteEmail("");
      setSelectedRole("");
      // Refresh user list after successful invite
      await refreshUsers();
      callSnackbar("success", t('userManagement.invite.bulkSuccess'));
    } catch (e) {
      callSnackbar("error", t('userManagement.invite.bulkFailed'));
    } finally {
      setSubmitting(false);
    }
  };
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isFullScreen) {
        setIsFullScreen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullScreen]);

  const table = useMantineReactTable({
    state: {
      isLoading: loading,
      isFullScreen,
      pagination,
    },
    onPaginationChange: setPagination,
    onIsFullScreenChange: setIsFullScreen,
    columns,
    data: filteredUsers,
    enableFullScreenToggle: true,
    enableStickyHeader: true,
    positionGlobalFilter: "right",
    initialState: {
      density: "xs",
      showGlobalFilter: true,
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pageSize,
      },
    },
    mantineTableContainerProps: {
      sx: {
        height: getTableHeight(),
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: false,
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableBodyProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        maxHeight: "1000px",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
      mantineSearchTextInputProps: {
      placeholder: t("userManagement.userTable.searchPlaceholder"),
      sx: { minWidth: "300px" },
      "data-intro": t('userManagement.searchIntro'),
      "data-position": "bottom",
    },
    mantineTopToolbarProps: {
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineBottomToolbarProps: {
      sx: {
        padding: "10px",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableBodyCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
        borderTop: "1px solid #dee2e6 !important",
      },
    },
    globalFilterModeOptions: ["fuzzy"],
      renderTopToolbarCustomActions: ({ table }) => (
      <Box
        className="box-header"
        style={{ display: "flex", alignItems: "center", gap: 16 }}
        data-intro={t('userManagement.inviteFilterIntro')}
        data-position="bottom"
      >
        <Button
          aria-label="user-management-invite-users"
          option="raised"
          disabled={!(userScope && userScope.access && userScope.access.addUsers)}
          color="seGreen.0"
          leftIcon={<SeIcon>action_add_flat</SeIcon>}
          onClick={addAsset}
          style={{
            color: !isLightMode ? "#2e3032" : "#ffffff",
          }}
          data-intro={t('userManagement.clickInvite')}
          data-position="bottom"
        >
          {t("userManagement.userTable.inviteUsers")}
        </Button>
        <Select
          data={statusOptions}
          value={statusFilter}
          onChange={changeUserStatus}
          placeholder={t("userManagement.userTable.status.placeholder")}
          sx={{ minWidth: 160 }}
          withinPortal
          searchable
          data-intro={t('userManagement.filterByStatus')}
          data-position="bottom"
        />
      </Box>
    ),
  });

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-1"></div>
          <div className="col-sm-11 mt-3">
            <div className="col-sm-11" style={{ height: "100%" }}>
              <MantineReactTable table={table} className="maintain-zoom" />
            </div>
          </div>
        </div>
      </div>

      <se-snackbar
        id="snackbar-register"
        type={notification.type === "error" ? "error" : "success"}
        icon="information_stroke"
        message={notification.message}
        duration="3000"
      ></se-snackbar>

      {inviteModalOpen && (
        <se-dialog
          id="dialog-invite-user"
          size="large"
          open={inviteModalOpen}
          can-backdrop="false"
        >
          <se-dialog-header>{t("userManagement.userTable.inviteUserTitle")}</se-dialog-header>
          <se-dialog-content className="se-dialog-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <div className="row">
                    <div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
                      <form>
                        <div className="row mb-3">
                          <div
                            className="col"
                            style={{ flex: 1, minWidth: 0, width: "100%" }}
                          >
                            <TextInput
                              label={t("userManagement.userTable.fields.email")}
                              placeholder={t("userManagement.userTable.fields.emailPlaceholder")}
                              value={inviteEmail}
                              onChange={(e) => {
                                setInviteEmail(e.currentTarget.value);
                                setInviteError("");
                              }}
                              required
                              error={inviteError}
                            />
                          </div>
                          <div
                            className="col"
                            style={{ flex: 1, minWidth: 0, width: "100%" }}
                          >
                            <Select
                              label={t("userManagement.userTable.fields.role")}
                              placeholder={t("userManagement.userTable.fields.rolePlaceholder")}
                              data={roleOptions}
                              value={selectedRole}
                              onChange={(value) => {
                                setSelectedRole(value);
                                setInviteError("");
                                setInviteDate("");
                              }}
                              withinPortal={true}
                              required
                              styles={{ input: { width: "100%" } }}
                              searchable
                            />
                          </div>
                        </div>
                        {/* Show date field and submit button in same row if role is selected and not secadmin */}
                        {(() => {
                          const selectedRoleObj = roles.find(
                            (role) => role.id === selectedRole || role.role === selectedRole
                          );
                          if (selectedRoleObj && selectedRoleObj.role.toLowerCase() !== "secadmin") {
                            return (
                              <div className="row mb-3 align-items-end">
                                <div
                                  className="col-6"
                                  
                                >
                                  <DateInput
                                    label={t("userManagement.userTable.fields.endDate")}
                                    value={inviteDate}
                                    onChange={setInviteDate}
                                    required
                                    placeholder={t("userManagement.userTable.fields.datePlaceholder")}
                                    popoverProps={{
                                      withinPortal: true,
                                      position: "bottom-start",
                                    }}
                                    minDate={new Date()}
                                  />
                                </div>
                                <div className="col-auto d-flex gap-2">
                                  <se-button
                                    aria-label="invite-user-inline-cancel"
                                    onClick={() => {
                                      setInviteModalOpen(false);
                                      setInviteEmail("");
                                      setSelectedRole("");
                                      setInviteDate("");
                                      setInviteList([]);
                                      setInviteError("");
                                    }}
                                  >
                                    {t("common.cancel")}
                                  </se-button>
                                  <se-button
                                    aria-label="invite-user-inline-save"
                                    color="primary"
                                    onClick={handleAddInvite}
                                    disabled={
                                      !inviteEmail ||
                                      !selectedRole ||
                                      submitting ||
                                      !inviteDate
                                    }
                                  >
                                    {submitting ? t("userManagement.userTable.inviting") || "Submitting..." : t("common.save")}
                                  </se-button>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </se-dialog-content>
          <se-dialog-footer>
            {/* Only show buttons in footer if role is secadmin or no date field is shown */}
            {(() => {
              const selectedRoleObj = roles.find(
                (role) => role.id === selectedRole || role.role === selectedRole
              );
              if (!selectedRoleObj || selectedRoleObj.role.toLowerCase() === "secadmin") {
                return (
                  <>
                      <se-button
                        aria-label="invite-user-footer-cancel"
                        onClick={() => {
                          setInviteModalOpen(false);
                          setInviteEmail("");
                          setSelectedRole("");
                          setInviteDate("");
                          setInviteList([]);
                          setInviteError("");
                        }}
                      >
                        {t("common.cancel")}
                      </se-button>
                      <se-button
                        aria-label="invite-user-footer-save"
                        color="primary"
                        onClick={handleAddInvite}
                        disabled={!inviteEmail || !selectedRole || submitting}
                      >
                        {submitting ? t("userManagement.userTable.inviting") || "Submitting..." : t("common.save")}
                      </se-button>
                  </>
                );
              }
              return null;
            })()}
          </se-dialog-footer>
        </se-dialog>
      )}

      {viewModalOpen && selectedUser && (
        <se-dialog
          id="dialog-view-user"
          size="large"
          open={viewModalOpen}
          can-backdrop="false"
        >
          <se-dialog-header>{t("userManagement.userTable.viewUserTitle")}</se-dialog-header>
          <se-dialog-content className="se-dialog-content">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col">
                  <strong>{t("userManagement.userTable.columns.id") ?? 'ID'}:</strong> {selectedUser.userId}
                </div>
                <div className="col">
                  <strong>{t("userManagement.userTable.columns.firstName") ?? 'Name'}:</strong> {selectedUser.name}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col">
                  <strong>{t("userManagement.userTable.fields.email") ?? 'Email'}:</strong> {selectedUser.email}
                </div>
                <div className="col">
                  <strong>{t("userManagement.userTable.columns.company") ?? 'Company'}:</strong> {selectedUser.employer}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col">
                  <strong>{t("userManagement.userTable.columns.phoneNumber") ?? 'Phone Number'}:</strong> {selectedUser.phoneNumber}
                </div>
                <div className="col">
                  <strong>{t("userManagement.userTable.status.label") ?? 'Status'}:</strong> {selectedUser.status}
                </div>
              </div>
            </div>
          </se-dialog-content>
          <se-dialog-footer>
            <se-button aria-label="view-user-close" onClick={() => setViewModalOpen(false)}>{t("common.close")}</se-button>
          </se-dialog-footer>
        </se-dialog>
      )}

      {editModalOpen && editUser && (
        <se-dialog
          id="dialog-edit-user"
          size="large"
          open={editModalOpen}
          can-backdrop="false"
        >
          <se-dialog-header>{t("userManagement.userTable.editUserTitle")}</se-dialog-header>
          <se-dialog-content className="se-dialog-content">
            <div className="container-fluid">
              <div className="row mb-3">
                <div className="col">
                        <TextInput
                    label={t("userManagement.userTable.fields.email")}
                    value={editUser.email}
                    readOnly
                    disabled
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col">
                  <Select
                    label={t("userManagement.userTable.fields.role")}
                    placeholder={t("userManagement.userTable.fields.rolePlaceholder")}
                    data={roleOptions}
                    value={editRole}
                    onChange={(value) => {
                      setEditRole(value);
                      setEditError("");
                      setEditSubscriptionDate("");
                    }}
                    required
                    error={editError}
                    withinPortal={true}
                    styles={{ input: { width: "100%" } }}
                    searchable
                  />
                </div>
              </div>
              {(() => {
                const roleObj = roles.find(r => r.role === editRole || r.id === editRole);
                if (roleObj && roleObj.role && roleObj.role.toLowerCase() !== 'secadmin') {
                  return (
                    <div className="row mb-3">
                      <div className="col">
                        <DateInput
                          label={t('userManagement.userTable.fields.subscriptionEndDate')}
                          value={editSubscriptionDate}
                          onChange={setEditSubscriptionDate}
                          required
                          placeholder={t('userManagement.userTable.fields.datePlaceholder')}
                          popoverProps={{ withinPortal: true, position: 'bottom-start' }}
                          minDate={new Date()}
                        />
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </se-dialog-content>
          <se-dialog-footer>
                  <se-button aria-label="edit-user-cancel" onClick={() => setEditModalOpen(false)}>
              {t("common.cancel")}
            </se-button>
            <se-button
              aria-label="edit-user-save"
              color="primary"
              onClick={async () => {
                if (!editRole) {
                  setEditError(t('userManagement.edit.roleRequired'));
                  return;
                }
                // Determine selected role object; roles array may hold objects with id & role
                const roleObj = roles.find(r => r.role === editRole || r.id === editRole);
                const requiresDate = roleObj && roleObj.role && roleObj.role.toLowerCase() !== 'secadmin';
                if (requiresDate && !editSubscriptionDate) {
                  setEditError(t('userManagement.edit.dateRequired'));
                  return;
                }
                setEditSubmitting(true);
                try {
                  // Map to roleId (if editRole is role name, find corresponding id)
                  let roleId = roleObj ? roleObj.id : editRole;
                  if (!roleId && roleObj && roleObj.id) roleId = roleObj.id;
                  const payload = { email: editUser.email, roleId };
                  if (requiresDate && editSubscriptionDate) {
                    let dateObj = typeof editSubscriptionDate === 'string' ? new Date(editSubscriptionDate) : editSubscriptionDate;
                    if (dateObj instanceof Date && !isNaN(dateObj)) {
                      const yyyy = dateObj.getFullYear();
                      const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
                      const dd = String(dateObj.getDate()).padStart(2, '0');
                      payload.endDate = `${yyyy}-${mm}-${dd}`;
                    } else {
                      payload.endDate = editSubscriptionDate;
                    }
                  }
                  // Call addUsers API similarly to invite flow (per requirement)
                  await apiSession.addUsers(payload);
                  // Refresh users list after update
                  await refreshUsers();
                  // Update local editUser role for immediate UI feedback
                  setEditUser({ ...editUser, role: roleObj?.role || editRole });
                  setEditModalOpen(false);
                  callSnackbar('success', t('userManagement.edit.success'));
                } catch (e) {
                  setEditError(t('userManagement.edit.failed'));
                  callSnackbar('error', t('userManagement.edit.failed'));
                } finally {
                  setEditSubmitting(false);
                }
              }}
              disabled={editSubmitting || (editRoleRequiresDate() && !editSubscriptionDate)}
            >
              {editSubmitting ? t("userManagement.userTable.saving") || 'Saving...' : t("common.save")}
            </se-button>
          </se-dialog-footer>
        </se-dialog>
      )}

      {reinviteModalOpen && reinviteUser && (
        <se-dialog
          id="dialog-reinvite-user"
          size="small"
          open={reinviteModalOpen}
          can-backdrop="false"
        >
          <se-dialog-header>{t("userManagement.userTable.reinviteTitle")}</se-dialog-header>
          <se-dialog-content className="se-dialog-content">
            <div className="container-fluid">
              <div className="row mb-3">
                <div className="col">
                  {t("userManagement.userTable.reinviteConfirm", { email: reinviteUser.email })}
                </div>
              </div>
              {/* Role selector for re-invite (allow choosing a different role) */}
              <div className="row mb-3">
                <div className="col">
                  <Select
                    label={t('userManagement.userTable.fields.role')}
                    placeholder={t('userManagement.userTable.fields.rolePlaceholder')}
                    data={roleOptions}
                    value={reinviteSelectedRole}
                    onChange={(value) => {
                      setReinviteSelectedRole(value);
                      setReinviteError("");
                      // clear date when switching role so user re-enters if needed
                      setReinviteDate("");
                    }}
                    withinPortal={true}
                    required
                    styles={{ input: { width: "100%" } }}
                    searchable
                  />
                </div>
              </div>

              {/* If selected role requires end date (non-secadmin) show date picker */}
              {(() => {
                // Determine the role object: prefer selected role then user role
                let roleObj = null;
                if (reinviteSelectedRole) {
                  roleObj = roles.find(r => r.id === reinviteSelectedRole || r.role === reinviteSelectedRole);
                }
                if (!roleObj && reinviteUser) {
                  roleObj = roles.find(r => r.id === reinviteUser?.roleId || r.role === reinviteUser?.role);
                }
                const requiresDate = roleObj && roleObj.role && roleObj.role.toLowerCase() !== 'secadmin';
                if (requiresDate) {
                  return (
                    <div className="row mb-3">
                      <div className="col">
                        <DateInput
                          label={t('userManagement.userTable.fields.endDate')}
                          value={reinviteDate}
                          onChange={(val) => { setReinviteDate(val); setReinviteError(""); }}
                          required
                          placeholder={t('userManagement.userTable.fields.datePlaceholder')}
                          popoverProps={{ withinPortal: true, position: 'bottom-start' }}
                          minDate={new Date()}
                        />
                        {reinviteError && <Text size="sm" color="red" mt={4}>{reinviteError}</Text>}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </se-dialog-content>
          <se-dialog-footer>
            <se-button aria-label="reinvite-user-cancel" onClick={() => { setReinviteModalOpen(false); setReinviteUser(null); setReinviteDate(""); setReinviteError(""); setReinviteSelectedRole(""); }}>{t("common.no")}</se-button>
            <se-button aria-label="reinvite-user-confirm" color="primary" onClick={handleConfirmReinvite} disabled={submitting}>
              {submitting ? t("userManagement.userTable.reinviting") : t("common.yes")}
            </se-button>
          </se-dialog-footer>
        </se-dialog>
      )}

      {deleteModalOpen && deleteUser && (
        <se-dialog
          id="dialog-delete-user"
          size="small"
          open={deleteModalOpen}
          can-backdrop="false"
        >
          <se-dialog-header>{t('userManagement.delete.title')}</se-dialog-header>
          <se-dialog-content className="se-dialog-content">
            <div className="container-fluid">
              <div className="row mb-3">
                <div className="col">
                  {statusFilter=== 'I' ? t('userManagement.delete.email.confirm', { email: deleteUser?.email }) : t('userManagement.delete.confirm', { name: deleteUser?.name })}
                </div>
              </div>
            </div>
          </se-dialog-content>
          <se-dialog-footer>
            <se-button aria-label="delete-user-cancel" onClick={() => setDeleteModalOpen(false)}>{t('common.no')}</se-button>
            <se-button
              aria-label="delete-user-confirm"
              color="primary"
              onClick={async () => {
                setDeleteSubmitting(true);
                try {
                  // Simulate API call to delete user
                  await apiSession.deleteUser(deleteUser.userId);
                  setDeleteModalOpen(false);
                  setDeleteUser(null);
                  // Refresh users list after delete
                  await refreshUsers();
                  callSnackbar("success", t('userManagement.delete.success'));
                } catch (e) {
                  callSnackbar("error", t('userManagement.delete.failed'));
                } finally {
                  setDeleteSubmitting(false);
                }
              }}
              disabled={deleteSubmitting}
            >
              {deleteSubmitting ? t('userManagement.delete.deleting') : t('common.yes')}
            </se-button>
          </se-dialog-footer>
        </se-dialog>
      )}
    </>
  );
};

export default UserTable;
